import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

export type Theme = 'dark' | 'light';
export type ThemePhase = 'idle' | 'retract' | 'flicker' | 'extend';

const STORAGE_KEY = 'swezey-theme';

type ThemeContextValue = {
  phase: ThemePhase;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'light') {
    root.dataset.theme = 'light';
  } else {
    delete root.dataset.theme;
  }
  root.style.colorScheme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'light' ? '#e7f0f4' : '#07080a');
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore quota / private mode */
  }
}

export function readTheme(): Theme {
  if (typeof document === 'undefined') {
    return 'dark';
  }
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<ThemePhase>('idle');
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    for (const id of timers.current) {
      window.clearTimeout(id);
    }
    timers.current = [];
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const toggle = useCallback(() => {
    if (phase !== 'idle') {
      return;
    }

    const next: Theme = readTheme() === 'dark' ? 'light' : 'dark';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      applyTheme(next);
      return;
    }

    clearTimers();
    setPhase('retract');

    timers.current.push(
      window.setTimeout(() => {
        setPhase('flicker');
        timers.current.push(
          window.setTimeout(() => {
            applyTheme(next);
          }, 200),
        );
        timers.current.push(
          window.setTimeout(() => {
            setPhase('extend');
            timers.current.push(
              window.setTimeout(() => {
                setPhase('idle');
              }, 420),
            );
          }, 560),
        );
      }, 300),
    );
  }, [clearTimers, phase]);

  return (
    <ThemeContext.Provider value={{ phase, toggle }}>
      {children}
      <div className={phase === 'flicker' ? 'theme-flicker theme-flicker--on' : 'theme-flicker'} aria-hidden="true" />
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }
  return ctx;
}
